// Ported from himitsuconfidential/downstack-practice (header.js `Game` class).
// Board convention kept identical to the original: board[row][col], row 0 =
// bottom, row 19 = top, y increases upward. This keeps generator/downstack.ts
// a faithful translation of the original generation algorithm.

import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  I_KICKS,
  JLTSZ_180_KICKS,
  JLTSZ_KICKS,
  PIECE_TYPES,
  SHAPE_TABLE,
  TSPIN_ALL_CORNERS,
  TSPIN_FRONT_CORNERS,
  type Cell,
  type Offset,
  type PieceType,
} from './constants';

export type Orientation = 0 | 1 | 2 | 3;

export interface PieceState {
  piece: PieceType;
  x: number;
  y: number;
  orientation: Orientation;
}

export type LastMove =
  | { type: 'none' }
  | { type: 'move' }
  | { type: 'drop' }
  | { type: 'rotate'; kind: 'cw' | 'ccw' | '180'; kickIndex: number };

export type Board = Cell[][];

export function createEmptyBoard(): Board {
  return Array.from({ length: BOARD_HEIGHT }, () => Array<Cell>(BOARD_WIDTH).fill('N'));
}

export function cloneBoard(board: Board): Board {
  return board.map((row) => row.slice());
}

export function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function newBag(): PieceType[] {
  return shuffle([...PIECE_TYPES]);
}

/** Cells occupied by `state`, shifted into absolute board coordinates. */
export function toShape(state: Pick<PieceState, 'piece' | 'orientation' | 'x' | 'y'>): Offset[] {
  const shape = SHAPE_TABLE[state.piece][state.orientation];
  return shape.map(([dx, dy]) => [dx + state.x, dy + state.y] as Offset);
}

/** Raw (unshifted) orientation-0 shape, for Hold/Next previews. */
export function previewShape(piece: PieceType): Offset[] {
  return SHAPE_TABLE[piece][0].slice();
}

export function isCollide(board: Board, state: PieceState, drawmode: boolean): boolean {
  for (const [col, row] of toShape(state)) {
    if (col < 0 || col > BOARD_WIDTH - 1 || row < 0 || row > BOARD_HEIGHT - 1) return true;
    const cell = board[row][col];
    if (drawmode ? cell === 'G' : cell !== 'N') return true;
  }
  return false;
}

export function moveLeft(board: Board, state: PieceState, drawmode: boolean): boolean {
  const prevX = state.x;
  state.x -= 1;
  if (!isCollide(board, state, drawmode)) return true;
  state.x = prevX;
  return false;
}

export function moveRight(board: Board, state: PieceState, drawmode: boolean): boolean {
  const prevX = state.x;
  state.x += 1;
  if (!isCollide(board, state, drawmode)) return true;
  state.x = prevX;
  return false;
}

export function moveToWall(board: Board, state: PieceState, drawmode: boolean, dir: -1 | 1): number {
  let moved = 0;
  while (dir === -1 ? moveLeft(board, state, drawmode) : moveRight(board, state, drawmode)) moved++;
  return moved;
}

const groupKicks = (piece: PieceType) => (['J', 'L', 'T', 'S', 'Z'] as PieceType[]).includes(piece) ? JLTSZ_KICKS : I_KICKS;

/** Rotate counter-clockwise (orientation+1). Mirrors header.js rotate_anticlockwise.
 * Returns the kick index used (0 = no kick needed), or null if the rotation failed. */
export function rotateCCW(board: Board, state: PieceState, drawmode: boolean): number | null {
  const prevO = state.orientation;
  const prevX = state.x;
  const prevY = state.y;
  state.orientation = ((state.orientation + 1) % 4) as Orientation;
  if (!isCollide(board, state, drawmode)) return 0;
  const table = groupKicks(state.piece);
  const kicks = table[prevO];
  for (let idx = 0; idx < kicks.length; idx++) {
    const [kx, ky] = kicks[idx];
    state.x += kx;
    state.y += ky;
    if (!isCollide(board, state, drawmode)) return idx + 1;
    state.x = prevX;
    state.y = prevY;
  }
  state.orientation = prevO;
  return null;
}

/** Rotate clockwise (orientation-1). Mirrors header.js rotate_clockwise — note
 * it reuses the SAME kick table as CCW (negated, indexed by the new orientation),
 * per the reference engine (see constants.ts JLTSZ_KICKS comment).
 * Returns the kick index used (0 = no kick needed), or null if the rotation failed. */
export function rotateCW(board: Board, state: PieceState, drawmode: boolean): number | null {
  const prevO = state.orientation;
  const prevX = state.x;
  const prevY = state.y;
  state.orientation = ((state.orientation + 3) % 4) as Orientation;
  if (!isCollide(board, state, drawmode)) return 0;
  const table = groupKicks(state.piece);
  const kicks = table[state.orientation];
  for (let idx = 0; idx < kicks.length; idx++) {
    const [kx, ky] = kicks[idx];
    state.x -= kx;
    state.y -= ky;
    if (!isCollide(board, state, drawmode)) return idx + 1;
    state.x = prevX;
    state.y = prevY;
  }
  state.orientation = prevO;
  return null;
}

export function rotate180(board: Board, state: PieceState, drawmode: boolean): number | null {
  const prevO = state.orientation;
  const prevX = state.x;
  const prevY = state.y;
  state.orientation = ((state.orientation + 2) % 4) as Orientation;
  if (!isCollide(board, state, drawmode)) return 0;
  if ((['J', 'L', 'T', 'S', 'Z'] as PieceType[]).includes(state.piece)) {
    const kicks = JLTSZ_180_KICKS[state.orientation];
    for (let idx = 0; idx < kicks.length; idx++) {
      const [kx, ky] = kicks[idx];
      state.x -= kx;
      state.y -= ky;
      if (!isCollide(board, state, drawmode)) return idx + 1;
      state.x = prevX;
      state.y = prevY;
    }
  }
  state.orientation = prevO;
  return null;
}

/** Drops `state` straight down until it would collide; mutates y. Returns cells fallen. */
export function dropToBottom(board: Board, state: PieceState, drawmode: boolean): number {
  let fallen = 0;
  for (;;) {
    state.y -= 1;
    if (isCollide(board, state, drawmode)) {
      state.y += 1;
      return fallen;
    }
    fallen++;
  }
}

/** Non-mutating: returns a copy of `state` dropped to its resting position. */
export function ghostState(board: Board, state: PieceState, drawmode: boolean): PieceState {
  const copy: PieceState = { ...state };
  dropToBottom(board, copy, drawmode);
  return copy;
}

export function lockPiece(board: Board, state: PieceState, fill: Cell = state.piece): void {
  for (const [col, row] of toShape(state)) board[row][col] = fill;
}

/** Clears full rows (row 0 = bottom), shifting everything above down. Returns count cleared. */
export function clearFullLines(board: Board): number {
  const fullRows: number[] = [];
  for (let row = BOARD_HEIGHT - 1; row >= 0; row--) {
    if (board[row].every((cell) => cell !== 'N')) fullRows.push(row);
  }
  for (const row of fullRows) {
    board.splice(row, 1);
    board.push(Array<Cell>(BOARD_WIDTH).fill('N'));
  }
  return fullRows.length;
}

export function getColumnHeights(board: Board): number[] {
  const heights: number[] = [];
  for (let col = 0; col < BOARD_WIDTH; col++) {
    let h = -1;
    for (let row = 0; row < BOARD_HEIGHT; row++) if (board[row][col] !== 'N') h = row;
    heights.push(h);
  }
  return heights;
}

export function getMaxHeight(board: Board): number {
  return Math.max(...getColumnHeights(board));
}

export function isPerfectClear(board: Board): boolean {
  return board.every((row) => row.every((cell) => cell === 'N'));
}

export type TSpinResult = 'full' | 'mini' | null;

/** 3-corner T-spin rule with the kick-4 full-T-spin promotion, ported from is_tspin(). */
export function detectTSpin(board: Board, state: PieceState, lastMove: LastMove): TSpinResult {
  if (state.piece !== 'T') return null;
  const inBounds = (x: number, y: number) => x >= 0 && x <= BOARD_WIDTH - 1 && y >= 0 && y <= BOARD_HEIGHT - 1;
  let frontCount = 0;
  for (const [dx, dy] of TSPIN_FRONT_CORNERS[state.orientation]) {
    const tx = state.x + dx;
    const ty = state.y + dy;
    if (!inBounds(tx, ty) || board[ty][tx] !== 'N') frontCount++;
  }
  let allCount = 0;
  for (const [dx, dy] of TSPIN_ALL_CORNERS) {
    const tx = state.x + dx;
    const ty = state.y + dy;
    if (!inBounds(tx, ty) || board[ty][tx] !== 'N') allCount++;
  }
  if (frontCount === 2 && allCount >= 3) return 'full';
  if (frontCount === 1 && allCount >= 3) {
    if (
      lastMove.type === 'rotate' &&
      lastMove.kind !== '180' &&
      lastMove.kickIndex === 4 &&
      (state.orientation === 1 || state.orientation === 3)
    ) {
      return 'full';
    }
    if (lastMove.type === 'rotate') return 'mini';
  }
  return null;
}

export interface ClearResult {
  linesCleared: number;
  tspin: TSpinResult;
  combo: number;
  b2b: number;
  perfectClear: boolean;
}

/**
 * Live-play game: board + current piece + hold + a fixed puzzle queue.
 * Unlike the reference engine's infinite 7-bag `Game`, puzzles here are a
 * finite, pre-generated piece sequence (see generator/downstack.ts) — once
 * the queue is exhausted the puzzle is won or lost, not endless.
 */
export class Game {
  board: Board;
  queue: PieceType[];
  cursor = 0;
  current: PieceState | null = null;
  holdPiece: PieceType | null = null;
  holdUsed = false;
  lastMove: LastMove = { type: 'none' };
  drawmode = false;

  combo = -1;
  b2b = -1;
  pc = false;
  lineClear = 0;
  totalLineClear = 0;
  totalPiece = 0;
  topOut = false;

  constructor(board: Board, queue: PieceType[]) {
    this.board = board;
    this.queue = queue;
    this.spawnNext();
  }

  get isQueueDone(): boolean {
    return this.cursor >= this.queue.length;
  }

  get ghost(): PieceState | null {
    return this.current ? ghostState(this.board, this.current, this.drawmode) : null;
  }

  /** Pieces after the active one — `queue[cursor]` is always the active piece, so this starts at cursor+1. */
  peekNext(count: number): (PieceType | null)[] {
    const out: (PieceType | null)[] = [];
    for (let i = 1; i <= count; i++) out.push(this.queue[this.cursor + i] ?? null);
    return out;
  }

  private spawnState(piece: PieceType): PieceState {
    return { piece, x: 4, y: 18, orientation: 0 };
  }

  spawnNext(): void {
    if (this.isQueueDone) {
      this.current = null;
      return;
    }
    const piece = this.queue[this.cursor];
    this.current = this.spawnState(piece);
    this.holdUsed = false;
    this.lastMove = { type: 'none' };
    if (isCollide(this.board, this.current, this.drawmode)) this.topOut = true;
  }

  moveLeft(): boolean {
    const piece = this.current;
    if (!piece) return false;
    const ok = moveLeft(this.board, piece, this.drawmode);
    if (ok) this.lastMove = { type: 'move' };
    return ok;
  }

  moveRight(): boolean {
    const piece = this.current;
    if (!piece) return false;
    const ok = moveRight(this.board, piece, this.drawmode);
    if (ok) this.lastMove = { type: 'move' };
    return ok;
  }

  moveToWall(dir: -1 | 1): number {
    const piece = this.current;
    if (!piece) return 0;
    const n = moveToWall(this.board, piece, this.drawmode, dir);
    if (n > 0) this.lastMove = { type: 'move' };
    return n;
  }

  rotateCW(): boolean {
    const piece = this.current;
    if (!piece) return false;
    const kickIndex = rotateCW(this.board, piece, this.drawmode);
    if (kickIndex === null) return false;
    this.lastMove = { type: 'rotate', kind: 'cw', kickIndex };
    return true;
  }

  rotateCCW(): boolean {
    const piece = this.current;
    if (!piece) return false;
    const kickIndex = rotateCCW(this.board, piece, this.drawmode);
    if (kickIndex === null) return false;
    this.lastMove = { type: 'rotate', kind: 'ccw', kickIndex };
    return true;
  }

  rotate180(): boolean {
    const piece = this.current;
    if (!piece) return false;
    const kickIndex = rotate180(this.board, piece, this.drawmode);
    if (kickIndex === null) return false;
    this.lastMove = { type: 'rotate', kind: '180', kickIndex };
    return true;
  }

  softDropStep(): boolean {
    const piece = this.current;
    if (!piece) return false;
    const prevY = piece.y;
    piece.y -= 1;
    if (isCollide(this.board, piece, this.drawmode)) {
      piece.y = prevY;
      return false;
    }
    this.lastMove = { type: 'drop' };
    return true;
  }

  hardDrop(): ClearResult {
    const piece = this.current;
    if (!piece) throw new Error('no active piece');
    dropToBottom(this.board, piece, this.drawmode);
    const tspin = detectTSpin(this.board, piece, this.lastMove);
    lockPiece(this.board, piece);
    this.cursor++;
    this.totalPiece++;
    const linesCleared = clearFullLines(this.board);
    this.lineClear = linesCleared;
    if (linesCleared > 0) {
      this.b2b = linesCleared === 4 || tspin ? this.b2b + 1 : -1;
      this.combo += 1;
      this.totalLineClear += linesCleared;
      this.pc = isPerfectClear(this.board);
    } else {
      this.combo = -1;
      this.pc = false;
    }
    const result: ClearResult = { linesCleared, tspin, combo: this.combo, b2b: this.b2b, perfectClear: this.pc };
    this.spawnNext();
    return result;
  }

  hold(): boolean {
    const piece = this.current;
    if (piece) {
      if (this.holdUsed) return false;
      const current = piece.piece;
      if (this.holdPiece === null) {
        this.holdPiece = current;
        this.cursor++;
        this.spawnNext();
      } else {
        const swap = this.holdPiece;
        this.holdPiece = current;
        this.current = this.spawnState(swap);
      }
      this.holdUsed = true;
      this.lastMove = { type: 'none' };
      return true;
    }
    // No active piece (the queue ran out) but a piece may still be waiting in
    // hold — e.g. the puzzle's pre-staged piece was never swapped in. Rescue
    // it so the puzzle's solution (which may rely on this) stays reachable.
    if (this.holdPiece !== null) {
      this.current = this.spawnState(this.holdPiece);
      this.holdPiece = null;
      this.holdUsed = false;
      this.lastMove = { type: 'none' };
      if (isCollide(this.board, this.current, this.drawmode)) this.topOut = true;
      return true;
    }
    return false;
  }

  /** True once there is no active piece and nothing left in hold — the puzzle attempt is over. */
  get isExhausted(): boolean {
    return this.current === null && this.holdPiece === null;
  }
}
